/** GraphQL contract for the preference-order half of the shared Pitch queue. */
import { gql } from "@apollo/client";
import type { ScoutingGetWatchlistPitchQuery } from "@/generated/graphql";

/**
 * Appends one Pitch connection page without replacing loaded members.
 * @param previous - Accumulated Pitch query result
 * @param next - Newly fetched Pitch query result
 * @returns One query result containing both member pages
 */
export const appendWatchlistPitchPage = (
  previous: ScoutingGetWatchlistPitchQuery,
  next: ScoutingGetWatchlistPitchQuery
): ScoutingGetWatchlistPitchQuery => {
  const previousPitch = previous.getWatchlistPitch;
  const nextPitch = next.getWatchlistPitch;
  if (!previousPitch || !nextPitch) return previous;
  return {
    ...previous,
    getWatchlistPitch: {
      ...nextPitch,
      members: {
        ...nextPitch.members,
        edges: [...previousPitch.members.edges, ...nextPitch.members.edges],
      },
    },
  };
};

/** Backend-enforced maximum page size for Pitch connections. */
export const PITCH_PAGE_SIZE = 100;

/** One Pitch page read, as Apollo's `refetch` / `fetchMore` resolve it. */
type PitchPageRead = Promise<{ readonly data: ScoutingGetWatchlistPitchQuery }>;

/** What a complete re-read of one watchlist's Pitch needs. */
interface CompletePitchReadOptions {
  readonly id: string;
  readonly pageSize: number;
  readonly refetch: (variables: {
    readonly id: string;
    readonly first: number;
  }) => PitchPageRead;
  /** Reads the page after one cursor; the caller owns its pagination guard. */
  readonly fetchPage: (after: string) => PitchPageRead;
}

/**
 * Re-reads the whole Pitch — every connection page — after a conflict, so the
 * adopted snapshot carries the complete authoritative order.
 * @param options - Identity, page size and the Apollo readers
 * @returns The complete Pitch, or null/undefined as the backend answered
 */
export const readCompleteWatchlistPitch = async (
  options: CompletePitchReadOptions
): Promise<ScoutingGetWatchlistPitchQuery["getWatchlistPitch"]> => {
  const { id, pageSize, refetch, fetchPage } = options;
  const collectRemainingPages = async (
    accumulated: ScoutingGetWatchlistPitchQuery
  ): Promise<ScoutingGetWatchlistPitchQuery> => {
    const pageInfo = accumulated.getWatchlistPitch?.members.pageInfo;
    if (!pageInfo?.hasNextPage || !pageInfo.endCursor) return accumulated;
    const next = await fetchPage(pageInfo.endCursor);
    return collectRemainingPages(
      appendWatchlistPitchPage(accumulated, next.data)
    );
  };
  const firstPage = (await refetch({ id, first: pageSize })).data;
  return (await collectRemainingPages(firstPage)).getWatchlistPitch;
};

/** Mutation document retained locally until the deployed schema is regenerated. */
export const UPDATE_WATCHLIST_PREFERENCE_ORDER = gql`
  mutation ScoutingUpdateWatchlistPreferenceOrder(
    $input: UpdateWatchlistPreferenceOrderInput!
  ) {
    updateWatchlistPreferenceOrder(input: $input) {
      watchlistId
      orderedPlayers {
        playerId
        rank
      }
      manualOrder
      pitchRevision
    }
  }
`;

/** Mutation result needed to reconcile an optimistic preference-order write. */
export interface UpdatePreferenceOrderData {
  readonly updateWatchlistPreferenceOrder: {
    readonly watchlistId: string;
    readonly orderedPlayers: ReadonlyArray<{
      readonly playerId: string;
      readonly rank: number;
    }>;
    readonly manualOrder: boolean;
    readonly pitchRevision: number;
  };
}

/** Variables accepted by the preference-order CAS mutation. */
export interface UpdatePreferenceOrderVariables {
  readonly input: {
    readonly watchlistId: string;
    readonly expectedPitchRevision: number;
    readonly orderedPlayerIds: readonly string[];
  };
}

/** The error shapes Apollo hands back, read without one subclass in mind. */
interface GraphQLErrorLike {
  readonly message?: string;
}

/**
 * The backend's own words for a refused write, when it gave any (SE-8389
 * AC6: "Couldn't re-apply: {backend message}").
 * @param error - Unknown error returned by Apollo
 * @returns The first GraphQL error message, else the thrown message
 */
export const readMutationErrorMessage = (
  error: unknown
): string | undefined => {
  const apolloError = error as {
    graphQLErrors?: readonly GraphQLErrorLike[];
    errors?: readonly GraphQLErrorLike[];
    message?: unknown;
  };
  const graphQLMessage = (apolloError.graphQLErrors ?? apolloError.errors)?.[0]
    ?.message;
  if (graphQLMessage) return graphQLMessage;
  return typeof apolloError.message === "string" && apolloError.message
    ? apolloError.message
    : undefined;
};

/**
 * Unwraps a mutation payload, or throws with the backend's message: under the
 * app client's `errorPolicy: "all"` a refused write resolves with a null field
 * and `errors`, and the refusal copy must survive that.
 * @param payload - The mutation's result field
 * @param errors - The result's GraphQL errors, if any
 * @param fallback - Message when the backend said nothing
 * @returns The payload
 */
export const readMutationPayload = <Payload>(
  payload: Payload | null | undefined,
  errors: readonly GraphQLErrorLike[] | undefined,
  fallback: string
): Payload => {
  if (payload) return payload;
  throw new Error(errors?.[0]?.message || fallback);
};

/**
 * Reads a GraphQL conflict code without coupling the controller to one Apollo
 * error subclass.
 * @param error - Unknown error returned by Apollo
 * @returns Whether the backend reported a Pitch revision conflict
 */
export const hasWatchlistPitchConflictCode = (error: unknown): boolean => {
  const apolloError = error as {
    graphQLErrors?: ReadonlyArray<{ extensions?: { code?: string } }>;
    errors?: ReadonlyArray<{ extensions?: { code?: string } }>;
  };
  const graphQLErrors = apolloError.graphQLErrors ?? apolloError.errors;
  return (
    graphQLErrors?.some(item =>
      [
        "WATCHLIST_PREFERENCE_ORDER_CONFLICT",
        "WATCHLIST_PITCH_ASSIGNMENT_CONFLICT",
        "WATCHLIST_FORMATION_CONFLICT",
      ].includes(item.extensions?.code ?? "")
    ) ?? false
  );
};
